import {useCallback, useMemo} from 'react'
import {useBabylistEnabled} from '../../../babylist-api'
import {useProductConfiguratorControl} from '../../../contexts/product-configurator'
import {useAddToCart} from '../../../hooks/use-add-to-cart'
import {useCommerceData} from '../../../hooks/use-commerce-data'
import {useNotifyMeModal} from '../../../hooks/use-notify-me-modal'
import {useRedirectLinkPartnerModal} from '../../../hooks/use-redirect-link-partner-modal'
import {findMainSetProduct, isProductOrderable, isProductSet} from '../../../utils/product-utils'
import {trackPdpConfigureButton, trackPdpShopNowButton} from '../../../tracking/analytics'
import {useIntl} from 'react-intl'
import {XpdpConfiguratorSteps} from '../../../partials/xpdp-configurator/utils/configurator-constants'

/**
 * XLeap PDP CTA orchestration hook.
 *
 * This hook centralizes CTA behavior (add to cart, notify me, configurator, baby list, partner redirect)
 * so multiple UI surfaces can consume the same action contract (for example cockpit CTA section and sticky bar)
 * while keeping those components mostly presentational.
 *
 * @param {object} params
 * @param {object} params.product
 * @param {boolean} [params.isLoading]
 * @param {Function} [params.onRecommendedProductClick]
 * @param {Function} [params.onBeforeCtaAction]
 * @returns {{
 *   product: object,
 *   isLoading: boolean,
 *   openConfigurator: {onClick: Function},
 *   notifyMe: {modalProps: object, onClick: Function},
 *   addToCart: {onClick: Function},
 *   babyList: {enabled: boolean, onClick: Function},
 *   ecommerceEnabled: boolean,
 *   partnerRedirect: {isAvailable: boolean, redirectProps: object}
 * }}
 */
const useXleapPdpViewModel = ({
    product,
    isLoading: isProductLoading,
    onRecommendedProductClick,
    onBeforeCtaAction
}) => {
    const {formatMessage} = useIntl()
    const {handleAddToCart, isLoading} = useAddToCart(product, {
        isLoading: isProductLoading,
        onRecommendedProductClick
    })

    const {startConfigurator: _startConfigurator} = useProductConfiguratorControl()

    const redirectProps = useRedirectLinkPartnerModal({product, onClick: onBeforeCtaAction})
    const {isAvailable} = redirectProps

    const babyListEnabled = useBabylistEnabled()
    const {isEcommerceEnabled} = useCommerceData()
    const {onOpen: onOpenNotifyMeModal, ...notifyMeModalProps} = useNotifyMeModal()

    const productMemo = useMemo(
        () => ({
            ...product,
            isOrderable: isProductOrderable(product)
        }),
        [product]
    )

    const startConfigurator = useCallback(
        (extraState = {}) => {
            const mainProduct = isProductSet(product) ? findMainSetProduct(product) : product
            // Pass main set product variation values in state for initial color matching
            _startConfigurator(product.c_configuratorConfig, {
                defaultVariationValues: mainProduct?.variationValues,
                ...extraState
            })
        },
        [product, _startConfigurator]
    )

    return {
        ecommerceEnabled: isEcommerceEnabled,
        product: productMemo,
        isLoading,
        openConfigurator: {
            onClick: () => {
                trackPdpConfigureButton(
                    formatMessage({
                        id: 'product_detail.cta.customize',
                        // changed for en_GB locale
                        defaultMessage: 'Customise'
                    })
                )
                startConfigurator()
            }
        },
        notifyMe: {
            modalProps: notifyMeModalProps,
            onClick: () => {
                onBeforeCtaAction && onBeforeCtaAction()
                onOpenNotifyMeModal(product)
            }
        },
        addToCart: {
            onClick: () => {
                if (product.c_displayTemplate === 'xpdp-configurator') {
                    trackPdpShopNowButton(
                        formatMessage({
                            id: 'product_view.button.shop_now',
                            defaultMessage: 'Shop Now'
                        })
                    )
                    startConfigurator({initialStep: XpdpConfiguratorSteps.Colors})
                } else {
                    onBeforeCtaAction && onBeforeCtaAction()
                    handleAddToCart()
                }
            }
        },
        babyList: {
            enabled: babyListEnabled,
            onClick: () => {
                onBeforeCtaAction && onBeforeCtaAction()
            }
        },
        partnerRedirect: {
            isAvailable,
            redirectProps
        }
    }
}

export default useXleapPdpViewModel
